import React from 'react';
import { Checkbox, Typography } from '@mui/material';
import { InfoOutlined, CalendarMonthOutlined } from '@mui/icons-material';
import clsx from 'clsx';
import dayjs from 'dayjs';

import { ReactComponent as ClockIcon } from '../../../../assets/chat/clock-icon.svg';
import { ReactComponent as EditSquareIcon } from '../../../../assets/chat/edit-square-icon.svg';
import './SchedulerPrefCheckItem.scss';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '../../../../data-access';
import { getDayjsLocaleForLanguage } from '../../../../i18n/dayjs';

interface SchedulerPrefCheckItemProps {
  title: string;
  duration: string; // duration string, e.g. '5h 30m'
  startTime?: string; // start time string, e.g. '7:45 AM'
  endTime?: string; // end time string, e.g. '8:00 AM'
  date?: string; // datetime or date for pill label
  selectable?: boolean;
  checked?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  onCheckChange?: (checked: boolean) => void;
  infoIconType?: 'info' | 'calendar';
  icon?: React.ReactNode; // emoji or icon for the task
}

const SchedulerPrefCheckItem: React.FC<SchedulerPrefCheckItemProps> = ({
  title,
  duration,
  startTime,
  endTime,
  date,
  selectable = true,
  checked = false,
  disabled = false,
  onClick = () => void 0,
  onCheckChange = () => void 0,
  infoIconType = 'info',
  icon,
}) => {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const dateLocale = getDayjsLocaleForLanguage(language);
  const handleCheckPress = (e: React.MouseEvent) => {
    if (!selectable) {
      return;
    }
    e.stopPropagation();
    onCheckChange(!checked);
  };

  const InfoIconComponent = infoIconType === 'calendar' ? CalendarMonthOutlined : InfoOutlined;

  const dateInfo = React.useMemo(() => {
    if (!date) {
      return null;
    }

    const d = dayjs(date);
    if (!d.isValid()) {
      return null;
    }

    const isToday = d.isSame(dayjs(), 'day');
    const isTomorrow = d.isSame(dayjs().add(1, 'day'), 'day');
    const isYesterday = d.isSame(dayjs().subtract(1, 'day'), 'day');
    const isPast = d.isBefore(dayjs(), 'day');

    if (isToday) {
      return { label: t('calendar.common.today'), dayType: 'future' };
    }

    if (isTomorrow) {
      return { label: t('calendar.common.tomorrow'), dayType: 'future' };
    }

    if (isYesterday) {
      return { label: t('calendar.common.yesterday'), dayType: 'past' };
    }

    return { label: d.locale(dateLocale).format('MMM D'), dayType: isPast ? 'past' : 'future' };
  }, [date, dateLocale, t]);

  return (
    <div
      className={clsx('scheduler-pref-check-item', {
        'scheduler-pref-check-item--checked': checked && selectable && !disabled,
        'scheduler-pref-check-item--disabled': !selectable || disabled,
        'scheduler-pref-check-item--selectable': selectable && !disabled,
      })}
      onClick={disabled ? undefined : onClick}
    >
      <div className="scheduler-pref-check-item__container">
        {/* Top row: checkbox, icon, start time, end time, edit icon */}
        <div className="scheduler-pref-check-item__top-row">
          <div className="scheduler-pref-check-item__left-content">
            {selectable ? (
              <div className="scheduler-pref-check-item__checkbox-container">
                <Checkbox
                  checked={checked}
                  onClick={handleCheckPress}
                  disabled={!selectable || disabled}
                  className={clsx('scheduler-pref-check-item__checkbox')}
                  sx={{
                    padding: 0,
                  }}
                />
                {infoIconType === 'calendar' && (
                  <CalendarMonthOutlined className="scheduler-pref-check-item__calendar-indicator" />
                )}
              </div>
            ) : (
              <InfoIconComponent
                className={clsx('scheduler-pref-check-item__info-icon', {
                  'scheduler-pref-check-item__info-icon--calendar': infoIconType === 'calendar',
                })}
              />
            )}
            {icon && <div className="scheduler-pref-check-item__icon">{icon}</div>}
          </div>
          <div className="scheduler-pref-check-item__time-section">
            {startTime &&
              endTime &&
              dateInfo?.dayType === 'future' && ( // Show time pill only for future date
                <>
                  <div className="scheduler-pref-check-item__time-pill">
                    <Typography
                      variant="caption"
                      className="scheduler-pref-check-item__time-pill-text"
                    >
                      {startTime}
                    </Typography>
                  </div>
                  <Typography
                    variant="caption"
                    className="scheduler-pref-check-item__time-separator"
                  >
                    :
                  </Typography>
                  <div className="scheduler-pref-check-item__time-pill">
                    <Typography
                      variant="caption"
                      className="scheduler-pref-check-item__time-pill-text"
                    >
                      {endTime}
                    </Typography>
                  </div>
                </>
              )}
            {dateInfo && (
              <div
                className={clsx('scheduler-pref-check-item__date-pill', {
                  'scheduler-pref-check-item__date-pill--future': dateInfo.dayType === 'future',
                  'scheduler-pref-check-item__date-pill--past': dateInfo.dayType === 'past',
                })}
              >
                <Typography variant="caption" className="scheduler-pref-check-item__date-pill-text">
                  {dateInfo.label}
                </Typography>
              </div>
            )}
            <EditSquareIcon className="scheduler-pref-check-item__edit-icon" />
          </div>
        </div>

        {/* Bottom row: title, duration, clock icon */}
        <div className="scheduler-pref-check-item__bottom-row">
          <Typography variant="body1" className="scheduler-pref-check-item__title" title={title}>
            {title}
          </Typography>
          {duration && (
            <div className="scheduler-pref-check-item__duration-section">
              <Typography variant="subtitle2" className="scheduler-pref-check-item__duration-text">
                {duration}
              </Typography>
              <ClockIcon className="scheduler-pref-check-item__clock-icon" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulerPrefCheckItem;
